import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import { AmountSummaryTable } from '@/components/Approval/@components/ExpensePreApproval';
import { AiFileUploader } from '@/components/Common/FileUpload';
import { APPROVAL_AI_EXPENSE_EXTRACTION } from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import PlusIcon from '@/public/icons/approval/plus.svg';
import {
  applyAiVendors,
  buildExpenseSubject,
  computeDisplaySummary,
  COST_TYPE_OPTIONS,
  createExpenseState,
  createVendor,
  EXPENSE_VENDOR_MAX,
  PERIOD_COST_TYPE,
  validateForCalculation,
  type CostType,
  type ExpenseAiVendor,
  type ExpensePreApprovalState,
  type ExpenseVendor,
} from '@/utils/approval/expensePreApproval';
import type { ApprovalFormValues } from '../types';
import useAiUploadAttachments from '../useAiUploadAttachments';
import type { ItemTableChangeKind } from './ItemTable';
import VendorBlock from './VendorBlock';
import { ActionRow, ExpenseSection } from './styles';
import {
  APPROVAL_SECTION_GAP,
  APPROVAL_SECTION_GAP_LG,
} from '@/components/Approval/@components/InfoSection';

/** 레거시 결과 shape — 배열 또는 {"1": {...}} 객체 */
const toAiVendors = (result: unknown): ExpenseAiVendor[] => {
  if (Array.isArray(result)) return result as ExpenseAiVendor[];
  if (result && typeof result === 'object') {
    return Object.values(result as Record<string, ExpenseAiVendor>);
  }
  return [];
};

/**
 * 사전 비용 품의(205) 본문 위 폼 — 레거시 appr_edit_forms_expense_pre_approval 동작 그대로.
 * 입력칸·선택칸 수정·AI 파일 선택은 계산 무효(저장 시 재계산 요구), 표는 마지막 계산값 유지.
 * 업체·행 추가/삭제는 계산 상태 유지 — 레거시 1807행 input·change 와 같음
 * 업체 삭제는 표만 즉시 재계산
 */
const ExpensePreApprovalField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const { watch, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = watch('expensePreApproval') ?? createExpenseState();

  // 입력칸·선택칸 수정과 AI 파일 선택은 계산 무효, 업체·행 추가/삭제는 유지(레거시 1807행 input·change)
  const update = useCallback(
    (
      fn: (prev: ExpensePreApprovalState) => ExpensePreApprovalState,
      kind: ItemTableChangeKind,
    ) => {
      const prev = getValues('expensePreApproval') ?? createExpenseState();
      const next = fn(prev);
      setValue(
        'expensePreApproval',
        kind === 'edit' ? { ...next, amountCalculated: false } : next,
        { shouldDirty: true },
      );
    },
    [getValues, setValue],
  );

  const handleCostTypeChange = (costType: CostType) => {
    update((prev) => ({ ...prev, costType }), 'edit');
    setValue('subject', buildExpenseSubject(costType), { shouldDirty: true });
  };

  const handleVendorChange = (next: ExpenseVendor, kind: ItemTableChangeKind) =>
    update(
      (prev) => ({
        ...prev,
        vendors: prev.vendors.map((v) => (v.id === next.id ? next : v)),
      }),
      kind,
    );

  const handleAddVendor = () => {
    if (state.vendors.length >= EXPENSE_VENDOR_MAX) {
      snackbar.showError({
        message: t('지급 업체는 최대 {{max}}개까지 등록할 수 있습니다.', {
          max: EXPENSE_VENDOR_MAX,
        }),
      });
      return;
    }
    update(
      (prev) => ({ ...prev, vendors: [...prev.vendors, createVendor()] }),
      'structure',
    );
  };

  const handleDeleteVendor = (id: string) =>
    dialog.show({
      message: t(
        '해당 업체 정보를 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.',
      ),
      confirmButtonText: t('삭제'),
      cancelButtonText: t('취소'),
      onConfirm: () =>
        update((prev) => {
          const vendors = prev.vendors.filter((v) => v.id !== id);
          return {
            ...prev,
            vendors,
            amountSummary: computeDisplaySummary(vendors),
          };
        }, 'structure'),
    });

  const focusViolation = (
    vendorId?: string,
    rowId?: string,
    field?: string,
  ) => {
    if (!vendorId || !field) return;
    const vendorEl = document.querySelector(`[data-vendor-id="${vendorId}"]`);
    const scope = rowId
      ? vendorEl?.querySelector(`[data-row-id="${rowId}"]`)
      : vendorEl;
    scope?.querySelector<HTMLInputElement>(`input[name="${field}"]`)?.focus();
  };

  const handleCalculate = () => {
    const current = getValues('expensePreApproval') ?? createExpenseState();
    const violation = validateForCalculation(current);
    if (violation) {
      snackbar.showError({ message: t(violation.message) });
      focusViolation(violation.vendorId, violation.rowId, violation.field);
      return;
    }
    setValue(
      'expensePreApproval',
      {
        ...current,
        amountSummary: computeDisplaySummary(current.vendors),
        amountCalculated: true,
      },
      { shouldDirty: true },
    );
  };

  // 레거시 — 폼 안 파일 선택(change)도 계산 무효
  const { onUploadStart, onUploadSettled, onUploaded } = useAiUploadAttachments(
    () => update((prev) => prev, 'edit'),
  );

  const handleAiResult = useCallback(
    (result: unknown) =>
      update((prev) => applyAiVendors(prev, toAiVendors(result)), 'structure'),
    [update],
  );

  const showPeriod = state.costType === PERIOD_COST_TYPE;

  return (
    <Box
      className="expense-pre-approval"
      sx={{ mb: `${APPROVAL_SECTION_GAP}px` }}
    >
      <ExpenseSection>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('비용구분')}</InfoSectionTitle>
        <Select
          fullWidth
          value={state.costType}
          options={COST_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.label),
          }))}
          onChange={(e) => handleCostTypeChange(e.target.value as CostType)}
          sx={approvalOutlineSelectSx}
        />
      </ExpenseSection>

      <ExpenseSection sx={{ mb: `${APPROVAL_SECTION_GAP_LG}px` }}>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('지급 정보')}</InfoSectionTitle>
        <AiFileUploader
          requestUrl={APPROVAL_AI_EXPENSE_EXTRACTION}
          getResultUrl={(id) => `${APPROVAL_AI_EXPENSE_EXTRACTION}/${id}`}
          documentType="expense"
          onUploadStart={onUploadStart}
          onUploadSettled={onUploadSettled}
          onUploaded={onUploaded}
          onResult={handleAiResult}
        />
      </ExpenseSection>

      <Box className="expense-info">
        {state.vendors.map((vendor) => (
          <VendorBlock
            key={vendor.id}
            vendor={vendor}
            showPeriod={showPeriod}
            canDelete={state.vendors.length > 1}
            onChange={handleVendorChange}
            onDelete={() => handleDeleteVendor(vendor.id)}
          />
        ))}
      </Box>

      <ActionRow>
        <BoxButton
          variant="secondary"
          startIcon={<PlusIcon width={12} height={12} />}
          onClick={handleAddVendor}
        >
          {t('업체 추가')}
        </BoxButton>
        <BoxButton variant="keyColor" onClick={handleCalculate}>
          {t('지급 총액 계산')}
        </BoxButton>
      </ActionRow>

      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('지급 총액')}</InfoSectionTitle>
        <AmountSummaryTable rows={state.amountSummary} />
      </Box>
    </Box>
  );
};

export default ExpensePreApprovalField;
